/* eslint-disable object-curly-newline */
import React from 'react';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { getComparator, stableSort } from '../utils';
import EnhancedTableHead from '../EnhancedTableHead';
import DashboardStub from '../../DashboardStub';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from '../DataTable.module.scss';

const defaultPaginationOptions = {
  rowsPerPageList: [25, 50, 100],
  initRowsPerPage: 25,
};

const getRowKey = (row) => {
  const key = row.id
    ?? row._id
    ?? row.queryText
    ?? (typeof row.query === 'string' ? row.query : null)
    ?? row.productId
    ?? (typeof row.name === 'string' ? row.name : null);

  return String(key);
};

export default function AnalyticTable({
  headCells,
  rows,
  hidePagination,
  paginationOptions = defaultPaginationOptions,
  orderById = 'total',
  emptyMessageId = 'search.health.preview.noData',
  fixedLayout,
  minWidth,
  interactiveRows,
  onRowActivate,
}) {
  const t = useLocalizedMessage();
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState(orderById);
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(paginationOptions.initRowsPerPage);

  const rowsCount = rows.length;

  if (rowsCount === 0) {
    return <DashboardStub variant="table" compact messageId={emptyMessageId} />;
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;
  const activeSortCell = headCells.find((cell) => cell.id === orderBy);
  const activeSortKey = activeSortCell?.sortKey || orderBy;

  return (
    <div className={styles.root}>

      <TableContainer className={styles.scroll}>
        <Table
          className={styles.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label={t('table.accessible.analytics')}
          style={{
            minWidth: minWidth || undefined,
            tableLayout: fixedLayout ? 'fixed' : undefined,
          }}
        >
          {fixedLayout && (
            <colgroup>
              {headCells.map((cell) => (
                <col key={cell.id} style={cell.width ? { width: cell.width } : undefined} />
              ))}
            </colgroup>
          )}
          <EnhancedTableHead
            headCells={headCells}
            classes={styles}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rowsCount}
          />
          <TableBody>
            {stableSort(rows, getComparator(order, activeSortKey))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const isItemSelected = isSelected(row.name);
                const isInteractive = interactiveRows && typeof onRowActivate === 'function';
                const handleRowClick = (event) => {
                  if (!isInteractive) return;
                  const nestedControl = event.target.closest?.(
                    'a, button, input, select, textarea, [role="button"], [role="link"]',
                  );
                  if (nestedControl && nestedControl !== event.currentTarget) return;
                  onRowActivate(row);
                };
                const handleRowKeyDown = (event) => {
                  if (!isInteractive || event.key !== 'Enter') return;
                  event.preventDefault();
                  onRowActivate(row);
                };
                return (
                  <TableRow
                    hover
                    aria-checked={isItemSelected}
                    className={`${styles.row} ${isInteractive ? styles.interactiveRow : ''}`.trim()}
                    data-interactive={isInteractive ? 'true' : undefined}
                    onClick={handleRowClick}
                    onKeyDown={handleRowKeyDown}
                    tabIndex={isInteractive ? 0 : -1}
                    key={getRowKey(row)}
                    selected={isItemSelected}
                  >
                    {
                      headCells.map((e) => (
                        <TableCell
                          key={e.id}
                          align={e.action ? 'center' : (e.numeric ? 'right' : 'left')}
                          className={`${e.compact ? styles.compactCell : ''} ${
                            e.action ? styles.actionCell : ''
                          }`.trim()}
                          component={e.id === 'query' ? 'th' : 'td'}
                          scope={e.id === 'query' ? 'row' : undefined}
                        >
                          {typeof e.render === 'function'
                            ? e.render(row[e.id], row)
                            : row[e.id]}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {
        hidePagination
          ? null
          : (
            <TablePagination
              className={styles.pagination}
              rowsPerPageOptions={paginationOptions.rowsPerPageList}
              component="div"
              count={rowsCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )

      }
    </div>
  );
}

AnalyticTable.defaultProps = {
  hidePagination: false,
  orderById: 'total',
  emptyMessageId: 'search.health.preview.noData',
  fixedLayout: false,
  minWidth: 0,
  interactiveRows: false,
  onRowActivate: null,
};
AnalyticTable.propTypes = {
  orderById: PropTypes.any,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  headCells: PropTypes.arrayOf(PropTypes.object).isRequired,
  hidePagination: PropTypes.bool,
  emptyMessageId: PropTypes.string,
  fixedLayout: PropTypes.bool,
  minWidth: PropTypes.number,
  interactiveRows: PropTypes.bool,
  onRowActivate: PropTypes.func,
};
